import {
  restoreErrorMessage,
  useRestoreCanvas,
  useRestoreWorkspace,
} from "@/api/archived";
import { DEFAULT_CANVAS_NAME, useCanvasById } from "@/api/canvas";
import { useCanvasFileRecords } from "@/api/fileRecords";
import "@/components/canvas-ground/canvas-ground.css";
import { InTrashBanner } from "@/components/InTrashBanner/InTrashBanner";
import { useSidebarContext } from "@/context/SidebarContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import {
  anchorCenterFromElement,
  CANVAS_ADD_CONTEXT_TRIGGER_SELECTOR,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/canvas/canvasRailAddContextEvents";
import { dispatchClearExplorerSelection } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/canvas/canvasRailSelectionEvents";
import { isCanvasIntrinsicRecord } from "@/object-actions/canvasIntrinsic";
import type { ObjectActionHandlers } from "@/object-actions/types";
import { useAddFromWorkspaceToCanvas } from "@/pages/canvas/hooks/useAddFromWorkspaceToCanvas";
import { buildQuickAddCursorFromClientPos } from "@/pages/canvas/utils/quickAddCursor";
import { resolveFileRecordFromList } from "@/pages/canvas/utils/resolveFileRecordFromList";
import { cn } from "@/utils";
import { downloadAsFile, exportToDocx } from "@/utils/markdownExport";
import type { FileRecord } from "@cohesive/api-client";
import { type Entity, getEntityData } from "@cohesive/ui-components";
import { useRedo, useUndo } from "@liveblocks/react";
import { Cursors } from "@liveblocks/react-flow";
import "@liveblocks/react-flow/styles.css";
import "@liveblocks/react-ui/styles.css";
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  type Edge,
  type Node,
  type NodeChange,
  type OnNodeDrag,
  ReactFlow,
  useNodesInitialized,
  useReactFlow,
  useStore,
  type Viewport,
  ViewportPortal,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { FileText } from "lucide-react";
import { AnimatePresence } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  CANVAS_GRID,
  CANVAS_PAN,
  CANVAS_SELECTION,
  CANVAS_SNAP,
  CANVAS_ZOOM,
  getCanvasCursorInteraction,
} from "./canvas-controls.config";
import "./canvas-styles.css";
// After the Liveblocks stylesheet above, so the scoped comment theme wins.
import "./comments/canvas-comments.css";
import { CanvasCommentComposer } from "./comments/CanvasCommentComposer";
import { CanvasCommentPinLayer } from "./comments/CanvasCommentPinLayer";
import { useCanvasComments } from "./comments/CanvasCommentsContext";
import { CanvasCommentThreadWindow } from "./comments/CanvasCommentThreadWindow";
import {
  CommentMarkerNode,
  hideCommentMarkers,
} from "./comments/CommentMarkerNode";
import {
  absoluteNodePosition,
  placementForDrop,
  placementForNodeComment,
} from "./comments/threadPlacement";
import { CanvasControlsToolbar } from "./components/CanvasControlsToolbar";
import { CanvasDrawLayer } from "./components/CanvasDrawLayer";
import { CanvasFloatingChatPanel } from "./components/CanvasFloatingChatPanel";
import { CanvasGridFade } from "./components/CanvasGridFade";
import { CanvasNodeContextMenu } from "./components/CanvasNodeContextMenu";
import { CanvasShareButton } from "./components/CanvasShareButton";
import { CanvasToolbar } from "./components/CanvasToolbar";
import { DeferredQuickComposeGhostPreview } from "./components/DeferredQuickComposeGhostPreview";
import { OnboardingSpotlight } from "./components/OnboardingSpotlight";
import { QuickChatComposer } from "./components/QuickChatComposer";
import { QuickVoiceComposer } from "./components/QuickVoiceComposer";
import { CanvasInsertMenu } from "./components/toolbar/primary/CanvasInsertMenu";
import { CANVAS_FILE_UPLOAD_ACCEPT } from "./constants/canvasFileUpload.constants";
import {
  CanvasChatSessionNodeWrapper,
  CanvasChatSessionProvider,
} from "./context/CanvasChatSessionContext";
import { useCanvas } from "./context/CanvasContext";
import { useCanvasFloatingChat } from "./context/CanvasFloatingChatContext";
import { usePublishCanvasNodesMirror } from "./context/CanvasNodesMirrorContext";
import {
  useCanvasOnboardingState,
  useCanvasOnboardingUpdate,
} from "./context/CanvasOnboardingContext";
import { withCanvasProvider } from "./context/CanvasProvider";
import { useCanvasRoute } from "./context/CanvasRouteContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "./context/CanvasSelectionCountContext";
import { usePublishCanvasSelectionMirror } from "./context/CanvasSelectionMirrorContext";
import { CanvasToolbarLayoutProvider } from "./context/CanvasToolbarLayoutContext";
import { useCanvasUi } from "./context/CanvasUiContext";
import { useHelperLines } from "./helper-lines/useHelperLines";
import {
  clearNodeHoveredAttribute,
  setNodeHoveredAttribute,
} from "./hooks/nodeHoverAttribute";
import { useCanvasAddContextPlacement } from "./hooks/useCanvasAddContextPlacement";
import { useCanvasCanvasDropTarget } from "./hooks/useCanvasCanvasDropTarget";
import { useCanvasCursorGlow } from "./hooks/useCanvasCursorGlow";
import { useCanvasDrawStyle } from "./hooks/useCanvasDrawStyle";
import { useCanvasEdgeHandlers } from "./hooks/useCanvasEdgeHandlers";
import { useCanvasFolderUpload } from "./hooks/useCanvasFolderUpload";
import type { ChromeInsertHandlers } from "./hooks/useCanvasInsertMenuActions";
import { useCanvasInsertMenuActions } from "./hooks/useCanvasInsertMenuActions";
import { useCanvasKeyboardShortcuts } from "./hooks/useCanvasKeyboardShortcuts";
import { useCanvasNodeDragHandlers } from "./hooks/useCanvasNodeDragHandlers";
import { useCanvasPaneClick } from "./hooks/useCanvasPaneClick";
import { useCanvasPermissions } from "./hooks/useCanvasPermissions";
import { useCanvasPreventSwipeBackNavigation } from "./hooks/useCanvasPreventSwipeBackNavigation";
import { useCanvasPreview } from "./hooks/useCanvasPreview";
import { useCanvasQuickAddHandlers } from "./hooks/useCanvasQuickAddHandlers";
import { useCanvasSessionRestore } from "./hooks/useCanvasSessionRestore";
import { useCanvasShapeAndEdgeStyle } from "./hooks/useCanvasShapeAndEdgeStyle";
import { useCanvasToolbarFileUpload } from "./hooks/useCanvasToolbarFileUpload";
import { useCanvasViewportStorage } from "./hooks/useCanvasViewportStorage";
import { useCanvasWidgetQuickPlace } from "./hooks/useCanvasWidgetQuickPlace";
import {
  CANVAS_OPEN_ADD_CONTEXT_EVENT,
  clearCanvasInitialFitViewSignal,
  emitCanvasInitialFitView,
  useCanvasWindowEvents,
} from "./hooks/useCanvasWindowEvents";
import { useCanvasWrapperLayout } from "./hooks/useCanvasWrapperLayout";
import { useCanvasZOrderActions } from "./hooks/useCanvasZOrderActions";
import { useConnectorPlacement } from "./hooks/useConnectorPlacement";
import { useCopyPaste } from "./hooks/useCopyPaste";
import { DOC_NODE_HEIGHT, DOC_NODE_WIDTH } from "./hooks/useCreateDocOnCanvas";
import {
  type DeferredQuickComposeMode,
  useDeferredQuickChatPlacement,
} from "./hooks/useDeferredQuickChatPlacement";
import { useDocPlacement } from "./hooks/useDocPlacement";
import { useEnsureCanvasAgentChatOpen } from "./hooks/useEnsureCanvasAgentChatOpen";
import { useExpandFileRecordOnArrival } from "./hooks/useExpandFileRecordOnArrival";
import { useFileRecordCreateFromURL } from "./hooks/useFileRecordCreateFromURL";
import { useNodeRightClickPan } from "./hooks/useNodeRightClickPan";
import { useOnboardingCanvasZoom } from "./hooks/useOnboardingCanvasZoom";
import { useQuickChat } from "./hooks/useQuickChat";
import {
  runAdoptionForNewSection,
  useSectionPlacement,
} from "./hooks/useSectionPlacement";
import { useSeedOnboardingWidget } from "./hooks/useSeedOnboardingWidget";
import { useShapePlacement } from "./hooks/useShapePlacement";
import { useTextLabelPlacement } from "./hooks/useTextLabelPlacement";
import {
  NOTE_NODE_HEIGHT,
  NOTE_NODE_WIDTH,
  useTextNotePlacement,
} from "./hooks/useTextNotePlacement";
import { ConnectorNode } from "./nodes/ConnectorNode";
import { DrawingNode } from "./nodes/DrawingNode";
import { EdgeFloatingToolbar } from "./nodes/EdgeFloatingToolbar";
import { EntityNode } from "./nodes/EntityNode";
import { LabeledEdge } from "./nodes/LabeledEdge";
import { MultiSelectFloatingToolbar } from "./nodes/MultiSelectFloatingToolbar";
import { SectionNode } from "./nodes/SectionNode";
import { ShapeNode } from "./nodes/ShapeNode";
import type { ShapeType } from "./nodes/shapes/index";
import { Shape } from "./nodes/shapes/Shape";
import {
  DEFAULT_EDGE_COLOR,
  DEFAULT_NOTE_COLOR,
} from "./nodes/shared/CanvasColorPalettes";
import { ChromeCollisionBridge } from "./nodes/shared/ChromeCollisionBridge";
import { TextLabelNode } from "./nodes/TextLabelNode";
import { TextLabelPlacementGhost } from "./nodes/TextLabelPlacementGhost";
import { TextNoteNode } from "./nodes/TextNoteNode";
import { WidgetNode } from "./nodes/WidgetNode";
import { WIDGET_ALL_PRESET_MAP } from "./nodes/widgets";
import { OnboardingOverlay } from "./nodes/widgets/onboarding-welcome/OnboardingOverlay";
import { connectionLineTypeFromCurve } from "./utils/applyEdgeStyle";
import { clearStrayDomSelection } from "./utils/domSelection";
import { drawingCursorTool } from "./utils/drawingTips";
import { nodeBounds, nodeBoundsById } from "./utils/nodeBounds";
import {
  multiSelectNodesSelector,
  selectedEdgesSelector,
} from "./utils/reactFlowSelectors";
// Stable node type map — defined outside the component so references NEVER
// change and React Flow never remounts nodes due to type-ref changes.
const NODE_TYPES = {
  entity: EntityNode,
  textNote: TextNoteNode,
  canvasText: TextLabelNode,
  chatSession: CanvasChatSessionNodeWrapper,
  canvasShape: ShapeNode,
  canvasConnector: ConnectorNode,
  canvasDrawing: DrawingNode,
  widgetNode: WidgetNode,
  section: SectionNode,
  commentMarker: CommentMarkerNode,
};

const EDGE_TYPES = {
  labeled: LabeledEdge,
};

// Fallback applied to edges that don't specify these fields. Intentionally no
// `markerEnd`/`markerStart` default: arrowheads come from the edge's own native
// markers (set by applyEdgeStyle on the client and by canvas_edit.go for the
// agent), so a missing marker must mean "no arrow" — not get force-defaulted.
const DEFAULT_EDGE_OPTIONS = {
  type: "labeled",
  style: {
    strokeWidth: 2,
    stroke: "hsl(var(--foreground) / 0.5)",
  },
} as const;

const FIT_VIEW_OPTIONS = {
  minZoom: CANVAS_ZOOM.minZoom,
  maxZoom: 1,
} as const;

const FALLBACK_VIEWPORT = { x: 0, y: 0, zoom: 1 } as const;

const RF_PRO_OPTIONS = { hideAttribution: true } as const;

function CanvasPageInner() {
  const reactFlowInstance = useReactFlow();
  /** Tracks the last known cursor position over the canvas (screen coords). */
  const lastCursorScreenPosRef = useRef<{ x: number; y: number } | null>(null);

  const { canvasId } = useCanvasRoute();
  const { data: canvasFileRecords = [] } = useCanvasFileRecords(canvasId);

  const canvas = useCanvas();

  const { railOpen, isDrawerOpen, toggleRail } = useSidebarContext();
  const lg = useBreakpoint("lg");
  // The canvas's contents live in the rail DRAWER (auto-active on this
  // route), so the explorer toggle simply opens/closes the rail.
  const isExplorerOpen = lg ? railOpen : isDrawerOpen;

  const handleToggleExplorer = useCallback(() => {
    if (!canvasId) return;
    toggleRail();
  }, [canvasId, toggleRail]);

  // ── Onboarding coordination ────────────────────────────────────────────
  // The onboarding widget reads steady-state booleans
  // (`isExplorerOpen`, `isAgentChatOpen`) and writes
  // `lockedViewport` / `highlightTargetId` via this context. The
  // shallow-equality check inside `update` keeps these per-render
  // syncs from churning context subscribers when nothing changed.
  const onboardingUpdate = useCanvasOnboardingUpdate();
  const { lockedViewport: onboardingLocksViewport } =
    useCanvasOnboardingState();
  useEffect(() => {
    onboardingUpdate({ isExplorerOpen });
  }, [isExplorerOpen, onboardingUpdate]);

  // `currentSessionId` is bound below (after `useCanvasFloatingChat`).
  // The publish effect for `isAgentChatOpen` therefore lives just
  // after that hook call to keep the dependency chain straightforward.

  // Warm the insert menu's integrations while the board settles. Every
  // add-context surface (toolbar Insert, quick-add, the rail's add-context)
  // renders that list, and its request walks every tool set — cold, it lands
  // after the menu is already open and the rows shove the menu around. An
  // idle prefetch costs one org-level request per canvas visit and makes all

  // Fetch current canvas for document title
  const { data: currentCanvas } = useCanvasById(canvasId);
  const canvasName = currentCanvas?.name || DEFAULT_CANVAS_NAME;

  const restoreCanvas = useRestoreCanvas({
    onSuccess: () => toast.success("Restored Canvas."),
    // 409 carries "restore the Workspace first" guidance from the server.
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Canvas")),
  });
  // A cascade-archived canvas (archived_with_resource_id set) can only come back via
  // its workspace — restores never cascade upward, so the banner restores
  // the workspace, honestly labeled.
  const restoreWorkspace = useRestoreWorkspace({
    onSuccess: () => toast.success("Restored Workspace."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Workspace")),
  });

  // Both verbs already account for `archived_at` — the hook is where the
  // trashed-canvas override lives, so the toolbar, top-bar rename, edge/node
  // editing, drop targets and every comment surface go read-only together.
  const { canEdit, canManageAssignments, canComment } = useCanvasPermissions();

  useCanvasPreview();
  usePublishCanvasSelectionMirror();
  useCanvasPreventSwipeBackNavigation();
  useSeedOnboardingWidget();
  useOnboardingCanvasZoom();
  // Restore chat + right pane when switching back to this canvas tab.
  useCanvasSessionRestore();

  // CanvasPage is the sole owner of the global OS-clipboard paste listener;
  // node/toolbar consumers of useCopyPaste only use copy/duplicate.
  const copyPaste = useCopyPaste({ registerOsPaste: true });
  const { fileInputRef, openFilePicker, handleFileSelect } =
    useCanvasToolbarFileUpload(canvasId);
  const ingestURL = useFileRecordCreateFromURL();
  const quickChat = useQuickChat();
  const {
    openSession: openCanvasFloatingSession,
    currentSessionId,
    isChatResizing,
  } = useCanvasFloatingChat();

  // Surface the floating chat's open/closed state into the onboarding
  // context. Treats "any session in the stack" as open; the AgentChat
  // step in the Product Photography flow advances on the flip to true.
  useEffect(() => {
    onboardingUpdate({ isAgentChatOpen: !!currentSessionId });
  }, [currentSessionId, onboardingUpdate]);

  const ensureCanvasAgentChatOpen = useEnsureCanvasAgentChatOpen();

  const { controlsMaxWidth, controlsRightOffset } = useCanvasWrapperLayout();

  const {
    reactFlowWrapperRef,
    setReactFlowWrapperEl,
    showGrid,
    gridMode,
    cursorTool,
    alignmentGuides,
    isWidgetsMenuOpen,
    setIsWidgetsMenuOpen,
    isLinkMenuOpen,
    setIsLinkMenuOpen,
    showQuickAdd,
    setShowQuickAdd,
    quickAddCursor,
    setQuickAddCursor,
    nodeContextMenu,
    setNodeContextMenu,
    isShapesMenuOpen,
    setIsShapesMenuOpen,
    activeConnectorPreset,
    setActiveConnectorPreset,
    isHoveringControls,
    setIsHoveringControls,
    setEditingEdgeId,
    setToolbarPopoverOpen,
  } = useCanvasUi();

  const cursorInteraction = useMemo(
    () => getCanvasCursorInteraction(cursorTool),
    [cursorTool],
  );

  // Chrome-profile inserts have no canvas cursor — same answer the rail's
  // "Add context" gives: anchor at the center of the visible canvas.
  const viewportCenterFlowPosition = useCallback(() => {
    const rect = reactFlowWrapperRef.current?.getBoundingClientRect();
    return reactFlowInstance.screenToFlowPosition(
      rect
        ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
        : { x: window.innerWidth / 2, y: window.innerHeight / 2 },
    );
  }, [reactFlowWrapperRef, reactFlowInstance]);

  const { folderInputRef, pickFolderAt, handleFolderSelect } =
    useCanvasFolderUpload(canvasId, viewportCenterFlowPosition);

  /** Sidebar / explorer “Add context” — drives the rail + button active state. */
  const [isExplorerQuickAddOpen, setIsExplorerQuickAddOpen] = useState(false);
  const [explorerAnchorToRailTrigger, setExplorerAnchorToRailTrigger] =
    useState(false);

  const closeQuickAdd = useCallback(() => {
    setShowQuickAdd(false);
    setQuickAddCursor(null);
    setIsExplorerQuickAddOpen(false);
    setExplorerAnchorToRailTrigger(false);
  }, [setShowQuickAdd, setQuickAddCursor]);

  const openExplorerQuickAdd = useCallback(
    (
      anchor: { clientX: number; clientY: number },
      options?: { anchorToRailTrigger?: boolean },
    ) => {
      // Rail opens have no canvas cursor. Anchor the cursor (used to place
      // Chat / Voice composers) at the center of the visible canvas instead of
      // the rail button; node-creating actions place at the next free viewport
      // slot regardless.
      const wrapperRect = reactFlowWrapperRef.current?.getBoundingClientRect();
      const cursorAnchor =
        options?.anchorToRailTrigger && wrapperRect
          ? {
              clientX: wrapperRect.left + wrapperRect.width / 2,
              clientY: wrapperRect.top + wrapperRect.height / 2,
            }
          : anchor;

      lastCursorScreenPosRef.current = {
        x: cursorAnchor.clientX,
        y: cursorAnchor.clientY,
      };

      const cursor = buildQuickAddCursorFromClientPos(
        reactFlowWrapperRef,
        reactFlowInstance.screenToFlowPosition,
        cursorAnchor.clientX,
        cursorAnchor.clientY,
      );

      setNodeContextMenu(null);
      setIsShapesMenuOpen(false);
      setIsWidgetsMenuOpen(false);
      setIsLinkMenuOpen(false);
      setQuickAddCursor(cursor);
      setShowQuickAdd(true);
      setIsExplorerQuickAddOpen(true);
      setExplorerAnchorToRailTrigger(options?.anchorToRailTrigger ?? false);
    },
    [
      reactFlowWrapperRef,
      reactFlowInstance,
      setNodeContextMenu,
      setIsShapesMenuOpen,
      setIsWidgetsMenuOpen,
      setIsLinkMenuOpen,
      setQuickAddCursor,
      setShowQuickAdd,
    ],
  );

  const handleExplorerToggleAddContext = useCallback(
    (
      anchor: { clientX: number; clientY: number },
      options?: { anchorToRailTrigger?: boolean },
    ) => {
      if (showQuickAdd) {
        closeQuickAdd();
        return;
      }
      openExplorerQuickAdd(anchor, options);
    },
    [showQuickAdd, closeQuickAdd, openExplorerQuickAdd],
  );

  /** Imperative open — idempotent; never closes an already-open explorer menu. */
  const handleExplorerOpenAddContext = useCallback(
    (
      anchor: { clientX: number; clientY: number },
      options?: { anchorToRailTrigger?: boolean },
    ) => {
      if (showQuickAdd) return;
      openExplorerQuickAdd(anchor, options);
    },
    [showQuickAdd, openExplorerQuickAdd],
  );

  // Double-click an edge to add/edit its label inline (Figma-style). Works on
  // unlabeled edges too — LabeledEdge shows an input once editing starts.
  const handleEdgeDoubleClick = useCallback(
    (_e: React.MouseEvent, edge: Edge) => {
      if (canEdit) setEditingEdgeId(edge.id);
    },
    [canEdit, setEditingEdgeId],
  );

  useEffect(() => {
    if (!canEdit) setNodeContextMenu(null);
  }, [canEdit, setNodeContextMenu]);

  // Canvas file → workspace copy, triggered from the node's right-click menu.
  // Resolve the right-clicked node's file record (if it is one) so the menu can
  // offer "Copy to workspace…" only for file-record nodes.
  const contextMenuFileRecord = useMemo<FileRecord | null>(() => {
    if (!nodeContextMenu) return null;
    const node = reactFlowInstance.getNode(nodeContextMenu.nodeId);
    const entity = (node?.data as { entity?: Entity } | undefined)?.entity;
    if (!entity || !("base" in entity) || entity.base !== "file_record") {
      return null;
    }
    const stub = getEntityData(entity) as FileRecord;
    return resolveFileRecordFromList(stub, canvasFileRecords) ?? stub;
  }, [canvasFileRecords, nodeContextMenu, reactFlowInstance]);

  const contextMenuEntityBase = useMemo(() => {
    if (!nodeContextMenu) return undefined;
    const node = reactFlowInstance.getNode(nodeContextMenu.nodeId);
    const entity = (node?.data as { entity?: Entity } | undefined)?.entity;
    if (!entity || !("base" in entity)) return undefined;
    return entity.base;
  }, [nodeContextMenu, reactFlowInstance]);

  const contextMenuEntity = useMemo(() => {
    if (!nodeContextMenu || nodeContextMenu.nodeType !== "entity") return null;
    const node = reactFlowInstance.getNode(nodeContextMenu.nodeId);
    return (node?.data as { entity?: Entity } | undefined)?.entity ?? null;
  }, [nodeContextMenu, reactFlowInstance]);

  // A block node offers Expand on top of a document's menu. Answered from
  // the node's display copy.
  const nodeContextMenuState = useMemo(() => {
    const node = nodeContextMenu
      ? reactFlowInstance.getNode(nodeContextMenu.nodeId)
      : undefined;
    return {
      isCanvasIntrinsic: isCanvasIntrinsicRecord({ nodeData: node?.data }),
    };
  }, [nodeContextMenu, reactFlowInstance]);

  // The Canvas Explorer pane lives in a sibling subtree to the canvas
  // page and can't reach into `CanvasUiContext`. The "Add context"
  // button in the rail dispatches `canvas:toggle-add-context` with the
  // clicked control as the menu anchor. Scoped by `canvasId` so multiple
  // workspaces in the same window don't cross-fire.
  useEffect(() => {
    if (!canvasId || !canEdit) return;
    const onToggleAddContext = (e: Event) => {
      const {
        canvasId: cid,
        anchorClientX,
        anchorClientY,
        anchorToRailTrigger,
      } = (e as CustomEvent).detail as {
        canvasId?: string;
        anchorClientX?: number;
        anchorClientY?: number;
        anchorToRailTrigger?: boolean;
      };
      if (cid && cid !== canvasId) return;
      if (anchorClientX == null || anchorClientY == null) return;
      handleExplorerToggleAddContext(
        {
          clientX: anchorClientX,
          clientY: anchorClientY,
        },
        { anchorToRailTrigger },
      );
    };
    window.addEventListener("canvas:toggle-add-context", onToggleAddContext);
    return () => {
      window.removeEventListener(
        "canvas:toggle-add-context",
        onToggleAddContext,
      );
    };
  }, [canvasId, canEdit, handleExplorerToggleAddContext]);

  // Mirror explorer add-context visibility so the rail button stays in sync.
  // Pane right-click quick add shares `showQuickAdd` but must not flip the rail.
  useEffect(() => {
    if (!canvasId) return;
    window.dispatchEvent(
      new CustomEvent("canvas:add-context-state", {
        detail: { canvasId, open: isExplorerQuickAddOpen },
      }),
    );
  }, [canvasId, isExplorerQuickAddOpen]);

  // Allow any component (e.g. onboarding steps) to imperatively open the
  // add-context menu without importing from CanvasPage.
  useEffect(() => {
    const handler = () => {
      const trigger = document.querySelector(
        CANVAS_ADD_CONTEXT_TRIGGER_SELECTOR,
      );
      let anchor: { clientX: number; clientY: number } | null =
        trigger instanceof HTMLElement
          ? anchorCenterFromElement(trigger)
          : null;
      if (!anchor) {
        const rect = reactFlowWrapperRef.current?.getBoundingClientRect();
        anchor = rect
          ? {
              clientX: rect.left + rect.width / 2,
              clientY: rect.top + rect.height / 2,
            }
          : {
              clientX: window.innerWidth / 2,
              clientY: window.innerHeight / 2,
            };
      }
      handleExplorerOpenAddContext(anchor, { anchorToRailTrigger: true });
    };
    window.addEventListener(CANVAS_OPEN_ADD_CONTEXT_EVENT, handler);
    return () =>
      window.removeEventListener(CANVAS_OPEN_ADD_CONTEXT_EVENT, handler);
  }, [handleExplorerOpenAddContext, reactFlowWrapperRef]);

  // Liveblocks undo/redo — keyboard shortcuts via `useCanvasKeyboardShortcuts`.
  // What's tracked: every storage mutation flowing through `useLiveblocksFlow`
  // (node/edge add, position, dimensions, delete, data writes) and our
  // `useMutation` calls. `@liveblocks/react-flow` also pauses history while a
  // drag/resize is in progress and resumes on `dragging:false` /
  // `resizing:false`, so a continuous gesture collapses into a single undo
  // entry. Cursor presence is NOT in history (we never pass `addToHistory`).
  // Mutations wrapped by `canvas.withoutUndo(...)` (e.g. node deletion paired
  // with irreversible file-record deletion) are skipped via `history.disable`.
  const undo = useUndo();
  const redo = useRedo();

  // Viewport persistence for tab-switching — save/restore scroll position per canvas.
  const { savedViewport, onViewportChange } = useCanvasViewportStorage();
  const { syncViewport: syncCursorGlowViewport } = useCanvasCursorGlow(
    reactFlowWrapperRef,
    { enabled: showGrid },
  );
  const handleViewportChange = useCallback(
    (viewport: Viewport) => {
      syncCursorGlowViewport(viewport);
      onViewportChange(viewport);
    },
    [syncCursorGlowViewport, onViewportChange],
  );
  // Marks the wrapper while a pan or zoom gesture is in flight (wheel zoom
  // included — React Flow's move start/end wrap every d3-zoom gesture) so
  // CSS can pause decorative animations (file-card skeleton shimmer) for
  // the duration. A plain attribute toggle, never React state: this fires
  // per gesture, not per frame, and nothing here needs to re-render.
  const handleMoveStart = useCallback(() => {
    reactFlowWrapperRef.current?.setAttribute("data-viewport-moving", "");
  }, [reactFlowWrapperRef]);
  const handleMoveEnd = useCallback(() => {
    reactFlowWrapperRef.current?.removeAttribute("data-viewport-moving");
  }, [reactFlowWrapperRef]);

  // Fit-to-content on first open only — fired imperatively (rather than via
  // React Flow's `fitView` prop, which stays queued on an empty canvas and then
  // snaps to the first node the user adds). The decision can't be locked at
  // mount either: Liveblocks hydrates `nodes` asynchronously, so the old
  // mount-time lazy-`useState` latched `false` before content arrived and the
  // canvas never fit. Fire exactly one fit, guarded by a ref:
  //   • saved viewport present → restore it, never fit. This covers switching
  //     back to an already-open tab; closing a tab clears its saved viewport
  //     (see clearViewport), so a reopen has none and falls through to the fit.
  //   • loaded + empty         → disarm, so adding the first node later doesn't
  //     yank the viewport.
  //   • loaded + has content   → fit once nodes are measured (`nodesInitialized`).
  // After fit resolves, emit `canvas:initial-fit-view` so e2e can wait on it.
  const nodesInitialized = useNodesInitialized();
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    clearCanvasInitialFitViewSignal();
    didInitialFitRef.current = false;
  }, [canvasId]);
  useEffect(() => {
    if (didInitialFitRef.current) return;
    if (savedViewport) {
      didInitialFitRef.current = true;
      return;
    }
    if (!canvas.isReady || canvas.isLoading) return;
    if (canvas.nodes.length === 0) {
      didInitialFitRef.current = true;
      return;
    }
    if (!nodesInitialized) return;
    if (!canvasId) return;
    didInitialFitRef.current = true;
    void reactFlowInstance.fitView(FIT_VIEW_OPTIONS).then(() => {
      emitCanvasInitialFitView({ canvasId });
    });
  }, [
    canvasId,
    savedViewport,
    canvas.isReady,
    canvas.isLoading,
    canvas.nodes.length,
    nodesInitialized,
    reactFlowInstance,
  ]);

  // Cursor presence is published by <Cursors /> (flow coordinates under the
  // `cursor` key, paused while panning). CanvasPage only tracks the screen
  // position for placement, and it must not subscribe to presence: a
  // subscription would re-render the whole page on every pointer move.
  useEffect(() => {
    const wrapper = reactFlowWrapperRef.current;
    if (!wrapper) return;
    const onPointerMove = (e: PointerEvent) => {
      lastCursorScreenPosRef.current = { x: e.clientX, y: e.clientY };
    };
    wrapper.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => wrapper.removeEventListener("pointermove", onPointerMove);
  }, [reactFlowWrapperRef]);

  const { batchUpdateNodeParents } = canvas;

  const handleAfterSectionAdded = useCallback(
    (
      sectionNode: Node,
      ghost: {
        position: { x: number; y: number };
        width: number;
        height: number;
      },
    ) => {
      runAdoptionForNewSection({
        sectionNode,
        ghost,
        getNodes: reactFlowInstance.getNodes,
        getInternalNode: reactFlowInstance.getInternalNode,
        batchUpdateNodeParents,
      });
    },
    [reactFlowInstance, batchUpdateNodeParents],
  );

  const {
    isPlacingSection,
    ghost: sectionGhost,
    cancelPlacing: cancelPlacingSection,
    handleToggleSectionMode,
  } = useSectionPlacement({
    onAfterSectionAdded: handleAfterSectionAdded,
  });

  // Text note placement mode (only enabled for users with edit permission)
  const {
    isPlacingTextNote,
    setIsPlacingTextNote,
    ghostPosition: ghostNotePosition,
    startTextNotePlacement,
    handleToggleTextNoteMode,
    handlePaneClick: handleTextNoteClick,
  } = useTextNotePlacement({ lastCursorScreenPosRef });

  // Doc placement — click mints an empty markdown file record that opens
  // in the collaborative editor.
  const {
    isPlacingDoc,
    setIsPlacingDoc,
    ghostPosition: ghostDocPosition,
    startDocPlacement,
    handleToggleDocMode,
    handlePaneClick: handleDocClick,
  } = useDocPlacement({ lastCursorScreenPosRef });

  // Canvas text placement — new nodes are added with selected: true.
  const {
    isPlacingTextLabel,
    setIsPlacingTextLabel,
    ghost: textLabelGhost,
    handleToggleTextLabelMode,
  } = useTextLabelPlacement();

  const {
    activeShapeColor,
    activeShapeStrokeColor,
    activeShapeNoFill,
    activeShapeStrokeStyle,
    activeShapeStrokeWidth,
    lastPlacedShape,
    lastEdgeStyle,
    setLastEdgeStyle,
    setLastPlacedShape,
  } = useCanvasShapeAndEdgeStyle();

  const connectionLineStyle = useMemo(
    () => ({
      stroke:
        lastEdgeStyle.color && lastEdgeStyle.color !== "default"
          ? lastEdgeStyle.color
          : DEFAULT_EDGE_COLOR,
      strokeWidth: lastEdgeStyle.stroke_width,
      strokeDasharray:
        lastEdgeStyle.stroke_style === "dashed"
          ? "16 8"
          : lastEdgeStyle.stroke_style === "dotted"
            ? "3 8"
            : undefined,
    }),
    [
      lastEdgeStyle.color,
      lastEdgeStyle.stroke_width,
      lastEdgeStyle.stroke_style,
    ],
  );

  // Connect two shape nodes with an editable edge
  const {
    isPlacingShape,
    pendingShapeType,
    ghost: shapeGhost,
    startPlacing: startPlacingShape,
    cancelPlacing: cancelPlacingShape,
  } = useShapePlacement({
    activeShapeColor,
    activeShapeNoFill,
    activeShapeStrokeStyle,
    activeShapeStrokeWidth,
    setLastPlacedShape: setLastPlacedShape,
  });

  // Connector placement
  const {
    isPlacingConnector,
    ghost: connectorGhost,
    startPlacing: startPlacingConnector,
    cancelPlacing: cancelPlacingConnector,
  } = useConnectorPlacement();

  // Free draw — the stroke gesture itself lives in CanvasDrawLayer, so its
  // ~60Hz samples never re-render this page.
  const [isDrawMenuOpen, setIsDrawMenuOpen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const { drawTip, drawErasing } = useCanvasDrawStyle();
  // The armed pen and its open flyout are one state: whichever path drops
  // draw mode puts the menu away with it, so no caller has to remember both.
  const cancelDrawing = useCallback(() => {
    setIsDrawing(false);
    setIsDrawMenuOpen(false);
  }, []);

  // nodes / edges / change handlers come from Liveblocks via CanvasLiveblocksContext.
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onDelete: liveblocksOnDelete,
  } = canvas;

  // Ref for canvas:add-entity handler so listener never re-registers
  const handleSelectEntityRef = useRef<((entity: Entity) => void) | null>(null);

  const {
    handleConnect,
    handleEdgesDelete,
    handleEdgeStyleChange,
    handleEdgeDelete,
  } = useCanvasEdgeHandlers({
    setLastEdgeStyle,
    lastEdgeStyle,
  });

  // All canvas: custom window events (session pane, skill notification, delete section, edges, nodes, etc.)
  useCanvasWindowEvents({
    canvasId,
    openCanvasFloatingSession,
    handleEdgeStyleChange,
    openChat: quickChat.openChat,
    handleSelectEntityRef,
    handleStartQuickChat: quickChat.handleStartQuickChat,
    ensureCanvasAgentChatOpen,
  });

  // A block opened from outside this canvas (search, a list, the detail
  // pane's redirect) arrives with an expand request in the navigation state.
  useExpandFileRecordOnArrival(nodes);

  // Mirror Liveblocks nodes for onboarding and explorer (outside Liveblocks tree).
  // Liveblocks is the persisted source of truth for `parentId`; React Flow's
  // internal store can lag after reparent writes (batchUpdateNodeParents
  // updates storage without a matching RF change frame).
  usePublishCanvasNodesMirror(canvasId, nodes, canvas.isReady);

  const isCanvasReady =
    !!savedViewport || (canvas.isReady && !canvas.isLoading);

  useEffect(() => {
    if (!isCanvasReady || !showGrid) return;
    syncCursorGlowViewport(reactFlowInstance.getViewport());
  }, [isCanvasReady, showGrid, syncCursorGlowViewport, reactFlowInstance]);

  const mergedNodes = useMemo(() => {
    // Apply each widget preset's `nodeProps` (selectable / deletable /
    // draggable / focusable) at render time so existing instances pick up
    // the flags even when their persisted Loro record predates the field.
    // Cheap: we're already iterating for ghost merge.
    const enhanced = hideCommentMarkers(nodes).map((n) => {
      if (n.type !== "widgetNode") return n;
      const presetId = String(
        (n.data as { presetId?: unknown })?.presetId ?? "",
      );
      const preset = WIDGET_ALL_PRESET_MAP.get(presetId);
      const np = preset?.nodeProps;
      if (!np) return n;
      return { ...n, ...np };
    });
    return enhanced;
  }, [nodes]);

  const { onNodeDrag, onNodeDragStop } = useCanvasNodeDragHandlers();

  // Alignment snapping (lattice ruling A5): guides beat the 16-grid within
  // capture radius while dragging a single node. Toggleable from the canvas
  // controls toolbar (persisted preference).
  const { rebuildIndex, updateHelperLines, HelperLines } = useHelperLines({
    enabled: alignmentGuides,
  });

  const onNodesChangeWithAlignment = useCallback(
    (changes: NodeChange<Node>[]) => {
      onNodesChange(
        updateHelperLines(
          changes,
          reactFlowInstance.getNodes(),
        ) as NodeChange<Node>[],
      );
    },
    [onNodesChange, updateHelperLines, reactFlowInstance],
  );

  const handleNodeDragStart = useCallback<OnNodeDrag>(() => {
    rebuildIndex(reactFlowInstance.getNodes());
  }, [rebuildIndex, reactFlowInstance]);

  const handleNodeDragStop = useCallback<OnNodeDrag>(
    (event, node, draggedNodes) => {
      onNodeDragStop(event, node, draggedNodes);
      rebuildIndex(reactFlowInstance.getNodes());
    },
    [onNodeDragStop, rebuildIndex, reactFlowInstance],
  );

  const { handleSelectEntity, getWebpagePlacement } =
    useCanvasAddContextPlacement();

  const comments = useCanvasComments();

  const {
    isPlacing: isPlacingDeferredQuickCompose,
    ghost: deferredQuickComposeGhost,
    beginPlacement: beginDeferredQuickComposePlacement,
    cancelPlacement: cancelDeferredQuickComposePlacement,
    commitAtScreen: commitDeferredQuickComposeAt,
  } = useDeferredQuickChatPlacement({
    lastCursorScreenPosRef,
    onStartQuickChat: quickChat.handleStartQuickChat,
    onStartQuickVoice: quickChat.handleStartQuickVoice,
    onStartComment: comments.placeDraftAt,
  });

  const multiSelectNodes = useStore(multiSelectNodesSelector);
  const selectedEdges = useStore(selectedEdgesSelector);
  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Edge toolbar: only when exactly one edge is selected and no nodes are
  // (marquee node-select often marks edges selected outside the box).
  const selectedEdgeForToolbar =
    canEdit &&
    !isMultiSelectToolbarOpen &&
    selectedNodeCount === 0 &&
    selectedEdges.length === 1
      ? selectedEdges[0]
      : null;

  const closeToolbarFlyouts = useCallback(() => {
    setIsShapesMenuOpen(false);
    setIsDrawMenuOpen(false);
    setIsWidgetsMenuOpen(false);
    setIsLinkMenuOpen(false);
  }, [setIsShapesMenuOpen, setIsWidgetsMenuOpen, setIsLinkMenuOpen]);

  const dismissToolbarMenus = useCallback(() => {
    closeToolbarFlyouts();
    closeQuickAdd();
    cancelDeferredQuickComposePlacement();
  }, [closeToolbarFlyouts, closeQuickAdd, cancelDeferredQuickComposePlacement]);

  const cancelAllPlacementModes = useCallback(() => {
    setIsPlacingTextNote(false);
    setIsPlacingDoc(false);
    setIsPlacingTextLabel(false);
    cancelPlacingShape();
    cancelPlacingConnector();
    cancelPlacingSection();
    cancelDrawing();
    cancelDeferredQuickComposePlacement();
  }, [
    setIsPlacingTextNote,
    setIsPlacingDoc,
    setIsPlacingTextLabel,
    cancelPlacingShape,
    cancelPlacingConnector,
    cancelPlacingSection,
    cancelDrawing,
    cancelDeferredQuickComposePlacement,
  ]);

  const handleInsertNewNote = useCallback(() => {
    setIsHoveringControls(false);
    setIsPlacingTextLabel(false);
    setIsPlacingDoc(false);
    cancelDeferredQuickComposePlacement();
    cancelPlacingShape();
    cancelPlacingConnector();
    cancelPlacingSection();
    setIsShapesMenuOpen(false);
    setIsWidgetsMenuOpen(false);
    setIsLinkMenuOpen(false);
    handleToggleTextNoteMode();
  }, [
    handleToggleTextNoteMode,
    setIsHoveringControls,
    setIsPlacingTextLabel,
    setIsPlacingDoc,
    cancelPlacingShape,
    cancelPlacingConnector,
    cancelPlacingSection,
    setIsShapesMenuOpen,
    setIsWidgetsMenuOpen,
    setIsLinkMenuOpen,
    cancelDeferredQuickComposePlacement,
  ]);

  const handleInsertNewPage = useCallback(() => {
    setIsHoveringControls(false);
    setIsPlacingTextLabel(false);
    setIsPlacingTextNote(false);
    cancelDeferredQuickComposePlacement();
    cancelPlacingShape();
    cancelPlacingConnector();
    cancelPlacingSection();
    setIsShapesMenuOpen(false);
    setIsWidgetsMenuOpen(false);
    setIsLinkMenuOpen(false);
    handleToggleDocMode();
  }, [
    handleToggleDocMode,
    setIsHoveringControls,
    setIsPlacingTextLabel,
    setIsPlacingTextNote,
    cancelPlacingShape,
    cancelPlacingConnector,
    cancelPlacingSection,
    setIsShapesMenuOpen,
    setIsWidgetsMenuOpen,
    setIsLinkMenuOpen,
    cancelDeferredQuickComposePlacement,
  ]);

  const handleInsertUploadFile = useCallback(() => {
    setIsHoveringControls(false);
    cancelAllPlacementModes();
    closeToolbarFlyouts();
    openFilePicker();
  }, [
    setIsHoveringControls,
    cancelAllPlacementModes,
    closeToolbarFlyouts,
    openFilePicker,
  ]);

  const handleInsertUploadFolder = useCallback(() => {
    setIsHoveringControls(false);
    cancelAllPlacementModes();
    closeToolbarFlyouts();
    pickFolderAt(viewportCenterFlowPosition());
  }, [
    setIsHoveringControls,
    cancelAllPlacementModes,
    closeToolbarFlyouts,
    pickFolderAt,
    viewportCenterFlowPosition,
  ]);

  const handleAddLinkSubmit = useCallback(
    (url: string) => {
      const placement = getWebpagePlacement();
      ingestURL({ sourceUrl: url, position: placement.position });
    },
    [getWebpagePlacement, ingestURL],
  );

  // Keep the ref current so the canvas:add-entity event handler always calls
  // the latest version of handleSelectEntity.
  handleSelectEntityRef.current = handleSelectEntity;

  const isPlacingObject =
    isPlacingTextNote ||
    isPlacingDoc ||
    isPlacingDeferredQuickCompose ||
    isPlacingTextLabel ||
    isPlacingShape ||
    isPlacingConnector ||
    isPlacingSection;

  const isPlacing = isPlacingObject || isDrawing;

  // One tool at a time. Every way into a placement mode — shortcut, toolbar,
  // insert menu — lands on these flags, so the pen is disarmed here rather
  // than in each entry point; left armed, its capture-phase listeners would
  // swallow the very click meant to place the thing just picked.
  useEffect(() => {
    if (isDrawing && isPlacingObject) cancelDrawing();
  }, [isDrawing, isPlacingObject, cancelDrawing]);

  // The mirror of that, and for the same reason: the deferred compose overlay
  // owns every click on the board, so arming anything else has to disarm it or
  // the next click drops a comment instead. Arming compose clears these in the
  // same batch, so this only fires when the other tool armed second.
  const isOtherToolArmed =
    isPlacingTextNote ||
    isPlacingDoc ||
    isPlacingTextLabel ||
    isPlacingShape ||
    isPlacingConnector ||
    isPlacingSection ||
    isDrawing ||
    isShapesMenuOpen ||
    isWidgetsMenuOpen ||
    isLinkMenuOpen;
  useEffect(() => {
    if (isPlacingDeferredQuickCompose && isOtherToolArmed) {
      cancelDeferredQuickComposePlacement();
    }
  }, [
    isPlacingDeferredQuickCompose,
    isOtherToolArmed,
    cancelDeferredQuickComposePlacement,
  ]);

  const {
    handlePaneContextMenu,
    handleNodeContextMenu,
    openNodeContextMenuAt,
    handleNodeContextMenuCopy,
    handleNodeContextMenuDuplicate,
    handleNodeContextMenuDeleteWrapperOnly,
    handleNodeContextMenuDelete,
    handleQuickAddSelectEntity,
  } = useCanvasQuickAddHandlers({
    isPlacingTextNote,
    isPlacingDoc,
    isPlacingDeferredQuickCompose,
    copyNodeIds: copyPaste.copyNodeIds,
    duplicateNodeIds: copyPaste.duplicateNodeIds,
    onQuickAddOpenedFromPane: () => setIsExplorerQuickAddOpen(false),
  });

  // Right-drag over a node pans (unmoved right-click still opens the node
  // menu). The `enabled` gate MUST mirror the ReactFlow `panOnDrag` prop
  // below — when right-button panning is off there, the gesture is off here.
  useNodeRightClickPan({
    enabled:
      !onboardingLocksViewport &&
      !isPlacingTextNote &&
      !isPlacingDoc &&
      !isPlacingDeferredQuickCompose &&
      !isPlacingSection &&
      cursorInteraction.panOnDragMouseButtons.includes(2),
    openNodeContextMenu: openNodeContextMenuAt,
  });

  const handleCanvasPaneClick = useCanvasPaneClick({
    isPlacing,
    isPlacingTextNote,
    handleTextNoteClick,
    isPlacingDoc,
    handleDocClick,
    paneSelectsOnClick: cursorInteraction.paneSelectsOnClick,
  });
  const handlePaneClick = useCallback(
    (e: React.MouseEvent) => {
      dispatchClearExplorerSelection();
      handleCanvasPaneClick(e);
    },
    [handleCanvasPaneClick],
  );

  const { handleQuickPlaceWidget } = useCanvasWidgetQuickPlace();

  const handleBeginDeferredQuickCompose = useCallback(
    (mode: DeferredQuickComposeMode) => {
      // The insert menu unmounts under the pointer, which can leave
      // isHoveringControls stuck true and suppress the ghost — the same reset
      // the note and doc entry points make.
      setIsHoveringControls(false);
      setIsPlacingTextNote(false);
      setIsPlacingDoc(false);
      setIsPlacingTextLabel(false);
      cancelPlacingShape();
      cancelPlacingConnector();
      cancelPlacingSection();
      cancelDrawing();
      closeToolbarFlyouts();
      closeQuickAdd();
      beginDeferredQuickComposePlacement(mode);
    },
    [
      setIsHoveringControls,
      setIsPlacingTextNote,
      setIsPlacingDoc,
      setIsPlacingTextLabel,
      cancelPlacingShape,
      cancelPlacingConnector,
      cancelPlacingSection,
      cancelDrawing,
      closeToolbarFlyouts,
      closeQuickAdd,
      beginDeferredQuickComposePlacement,
    ],
  );

  const handleBeginCommentPlacement = useCallback(
    () => handleBeginDeferredQuickCompose("comment"),
    [handleBeginDeferredQuickCompose],
  );

  // Right-click insert already knows the point, so the pin drops there
  // directly — the ghost gesture is for the toolbar and the keybinding.
  const handleCommentAtFlowPosition = useCallback(
    (flowPosition: { x: number; y: number }) => {
      comments.placeDraftAt(
        placementForDrop(
          flowPosition,
          reactFlowInstance.getNodes().map(nodeBounds),
        ),
      );
    },
    [comments, reactFlowInstance],
  );

  // Commenting on a node anchors to it outright: the object is the target, so
  // the pin travels with it. A right-click aimed at a point, so the pin lands
  // there; a menu opened from a toolbar button did not, so it takes the
  // node's top-right corner.
  const handleCommentOnNode = useCallback(
    (nodeId: string, point?: { x: number; y: number }) => {
      const nodesById = nodeBoundsById(reactFlowInstance.getNodes());
      const node = nodesById.get(nodeId);
      const absolute = absoluteNodePosition(nodeId, nodesById);
      if (!node || !absolute) return;
      comments.placeDraftAt(placementForNodeComment(node, absolute, point));
    },
    [comments, reactFlowInstance],
  );

  const chromeInsertHandlers = useMemo(
    (): ChromeInsertHandlers => ({
      onNewNote: handleInsertNewNote,
      onNewDocument: handleInsertNewPage,
      onUploadFile: handleInsertUploadFile,
      onUploadFolder: handleInsertUploadFolder,
      onAddLinkSubmit: handleAddLinkSubmit,
      onAddWidget: handleQuickPlaceWidget,
      onSelectEntityViewport: handleSelectEntity,
    }),
    [
      handleInsertNewNote,
      handleInsertNewPage,
      handleInsertUploadFile,
      handleInsertUploadFolder,
      handleAddLinkSubmit,
      handleQuickPlaceWidget,
      handleSelectEntity,
    ],
  );

  const onAddFromLibrary = useAddFromWorkspaceToCanvas(canvasId);
  // Board right-click: the picked file lands at the click's flow position
  // (captured when the menu opened). Toolbar keeps the next-free-spot.
  const onAddFromLibraryAnchored = useMemo(
    () =>
      onAddFromLibrary
        ? {
            workspaceId: onAddFromLibrary.workspaceId,
            onPick: (record: FileRecord) =>
              onAddFromLibrary.onPick(
                record,
                quickAddCursor?.flowPosition ?? undefined,
              ),
          }
        : undefined,
    [onAddFromLibrary, quickAddCursor],
  );

  const {
    actions: toolbarInsertActions,
    handleIntegrationEntitySelect: toolbarIntegrationSelect,
  } = useCanvasInsertMenuActions({
    context: {
      source: "toolbar",
      placement: "chrome",
      canvasId,
      cursor: null,
    },
    chrome: chromeInsertHandlers,
    startTextNotePlacement,
    onSelectEntityAtPosition: handleQuickAddSelectEntity,
    pickFolderAt,
    onStartQuickChat: quickChat.handleStartQuickChat,
    onStartQuickVoice: quickChat.handleStartQuickVoice,
    beginDeferredQuickCompose: handleBeginDeferredQuickCompose,
    canComment,
  });

  const anchoredInsertPlacement = explorerAnchorToRailTrigger
    ? "chrome"
    : "contextMenu";

  const {
    actions: anchoredInsertActions,
    handleIntegrationEntitySelect: anchoredIntegrationSelect,
    ingestContextMenuFiles,
  } = useCanvasInsertMenuActions({
    context: {
      source: explorerAnchorToRailTrigger ? "rail" : "paneContextMenu",
      placement: anchoredInsertPlacement,
      canvasId,
      cursor: quickAddCursor,
    },
    onClose: closeQuickAdd,
    chrome: chromeInsertHandlers,
    startTextNotePlacement,
    onSelectEntityAtPosition: handleQuickAddSelectEntity,
    pickFolderAt,
    onPasteAtPosition: copyPaste.pasteAt,
    onStartQuickChat: quickChat.handleStartQuickChat,
    onStartQuickVoice: quickChat.handleStartQuickVoice,
    beginDeferredQuickCompose:
      anchoredInsertPlacement === "chrome"
        ? handleBeginDeferredQuickCompose
        : undefined,
    onStartCommentAtPosition: handleCommentAtFlowPosition,
    canComment,
  });

  useCanvasKeyboardShortcuts({
    lastCursorScreenPosRef,
    onStartComment: handleBeginCommentPlacement,
    onCancelComment: cancelDeferredQuickComposePlacement,
    quickChat,
    copyPaste,
    undo,
    redo,
    isPlacingTextLabel,
    isPlacingTextNote,
    isPlacingDoc,
    setIsPlacingTextLabel,
    setIsPlacingTextNote,
    setIsPlacingDoc,
    handleToggleTextLabelMode,
    handleToggleTextNoteMode,
    handleToggleDocMode,
    cancelPlacingShape,
    cancelPlacingConnector,
    cancelDrawing,
    cancelPlacingSection,
    handleToggleSectionMode,
    lastPlacedShape,
    startPlacingShape: (s: string) => startPlacingShape(s as ShapeType),
    onToggleExplorer: handleToggleExplorer,
    onInsertNewNote: handleInsertNewNote,
    onInsertNewPage: handleInsertNewPage,
    onInsertUploadFile: handleInsertUploadFile,
  });

  const { handleNodeBringToFront, handleNodeSendToBack } =
    useCanvasZOrderActions();

  // The text note's Download expands into a format submenu (variants
  // attached in useCanvasEntityObjectActions), same as file records.
  const textNoteDownload = useMemo(() => {
    if (!nodeContextMenu) return null;
    const noteData = () =>
      (reactFlowInstance.getNode(nodeContextMenu.nodeId)?.data ?? {}) as {
        title?: string;
        content?: string;
      };
    return {
      exportMarkdown: () => {
        const d = noteData();
        downloadAsFile(
          d.content ?? "",
          `${d.title || "note"}.md`,
          "text/markdown",
        );
      },
      exportWord: () => {
        const d = noteData();
        void exportToDocx(d.content ?? "", d.title || "note");
      },
    };
  }, [nodeContextMenu, reactFlowInstance]);

  const nodeContextMenuHandlers = useMemo((): ObjectActionHandlers => {
    if (!nodeContextMenu) return {};
    const nodeId = nodeContextMenu.nodeId;
    return {
      copy: () => handleNodeContextMenuCopy(nodeId),
      duplicate: () => handleNodeContextMenuDuplicate(nodeId),
      delete: () => handleNodeContextMenuDelete(nodeId),
      // Hide on canvas — non-destructive: the node keeps existing (and a file
      // entity keeps its membership); the surface just stops rendering it.
      // Un-hiding lives on the canvas rail ("Show on canvas").
      "remove-from-canvas": () => canvas.setNodeHidden(nodeId, true),
      // Expand — a block's second view; the manifest hides it elsewhere.
      expand: () => canvas.expandNode(nodeId),
      "delete-wrapper-only": () =>
        handleNodeContextMenuDeleteWrapperOnly(nodeId),
      "bring-to-front": () => handleNodeBringToFront(nodeId),
      "send-to-back": () => handleNodeSendToBack(nodeId),
      // Resolution drops handler-less actions, so `can_comment` gates the row
      // by withholding the handler.
      ...(canComment
        ? {
            comment: () =>
              handleCommentOnNode(
                nodeId,
                nodeContextMenu.origin === "pointer"
                  ? nodeContextMenu.flowPosition
                  : undefined,
              ),
          }
        : {}),
      // Resolution drops handler-less actions; the variants attached later
      // replace this direct handler with the format submenu.
      ...(textNoteDownload
        ? { download: textNoteDownload.exportMarkdown }
        : {}),
    };
  }, [
    canComment,
    canvas,
    handleCommentOnNode,
    handleNodeBringToFront,
    handleNodeContextMenuCopy,
    handleNodeContextMenuDuplicate,
    handleNodeContextMenuDeleteWrapperOnly,
    handleNodeContextMenuDelete,
    handleNodeSendToBack,
    nodeContextMenu,
    textNoteDownload,
  ]);

  const {
    isDraggingFile,
    setBoardRef,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
  } = useCanvasCanvasDropTarget();

  return (
    <>
      <title>{canvasId ? canvasName : "Workspace"}</title>
      <div className="w-full h-full relative bg-surface-main">
        <div
          ref={(el) => {
            reactFlowWrapperRef.current = el;
            setReactFlowWrapperEl(el);
            // Hand the same DOM node to dnd-kit so explorer drags can
            // hit-test the canvas board as a droppable.
            setBoardRef(el);
          }}
          className={cn(
            "canvas-cursor-glow w-full h-full @container",
            showGrid && "canvas-cursor-glow--grid",
            isCanvasReady ? "opacity-100" : "opacity-0",
            (isPlacingTextNote ||
              isPlacingDoc ||
              isPlacingDeferredQuickCompose) &&
              "cursor-none",
            isPlacingSection && "cursor-crosshair",
            isPlacingTextLabel && "canvas-placing-crosshair",
            isDraggingFile && "ring-2 ring-canvas-selection ring-inset",
          )}
          data-cursor-tool={
            isDrawing
              ? drawingCursorTool(drawTip, drawErasing)
              : isPlacing
                ? undefined
                : cursorTool
          }
          // A document range no editor owns is only paint (WebKit fills
          // whole notes and empty canvas for it) and would otherwise survive
          // node clicks and drags, which prevent the mousedown default.
          onPointerDownCapture={clearStrayDomSelection}
          onClick={() => {
            // Close dialogs when clicking anywhere on canvas
            if (showQuickAdd) {
              closeQuickAdd();
            }
            if (nodeContextMenu) {
              setNodeContextMenu(null);
            }
            if (isWidgetsMenuOpen) {
              setIsWidgetsMenuOpen(false);
            }
            if (isLinkMenuOpen) {
              setIsLinkMenuOpen(false);
            }
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CanvasFloatingChatPanel />
          <CanvasChatSessionProvider value={quickChat.chatSessionContextValue}>
            <ReactFlow
              nodes={mergedNodes}
              edges={edges}
              onNodesChange={onNodesChangeWithAlignment}
              onEdgesChange={onEdgesChange}
              onNodeDragStart={handleNodeDragStart}
              onNodeMouseEnter={setNodeHoveredAttribute}
              onNodeMouseLeave={clearNodeHoveredAttribute}
              onNodeDrag={onNodeDrag}
              onNodeDragStop={handleNodeDragStop}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              onConnect={handleConnect}
              onDelete={liveblocksOnDelete}
              onEdgesDelete={handleEdgesDelete}
              onEdgeDoubleClick={canEdit ? handleEdgeDoubleClick : undefined}
              onPaneClick={handlePaneClick}
              onPaneContextMenu={handlePaneContextMenu}
              onNodeContextMenu={canEdit ? handleNodeContextMenu : undefined}
              selectionMode={CANVAS_SELECTION.mode}
              multiSelectionKeyCode={
                CANVAS_SELECTION.multiSelectionKeys as unknown as string[]
              }
              onlyRenderVisibleElements={false}
              selectionOnDrag={
                cursorInteraction.selectionOnDrag &&
                !isPlacingTextNote &&
                !isPlacingDoc &&
                !isPlacingDeferredQuickCompose &&
                !isPlacingTextLabel &&
                !isPlacingShape &&
                !isPlacingConnector &&
                !isDrawing &&
                !isPlacingSection
              }
              panOnScroll={
                onboardingLocksViewport ||
                isPlacingTextNote ||
                isPlacingDoc ||
                isPlacingDeferredQuickCompose ||
                isPlacingSection
                  ? false
                  : CANVAS_PAN.onScroll
              }
              zoomOnScroll={
                onboardingLocksViewport ? false : CANVAS_ZOOM.onScroll
              }
              zoomOnDoubleClick={
                onboardingLocksViewport ? false : CANVAS_ZOOM.onDoubleClick
              }
              zoomOnPinch={
                onboardingLocksViewport ? false : CANVAS_ZOOM.onPinch
              }
              panOnDrag={
                onboardingLocksViewport ||
                isPlacingTextNote ||
                isPlacingDoc ||
                isPlacingDeferredQuickCompose ||
                isPlacingSection
                  ? false
                  : cursorInteraction.panOnDragMouseButtons
              }
              nodesConnectable={canEdit && cursorInteraction.nodesConnectable}
              connectionMode={ConnectionMode.Loose}
              connectionLineType={connectionLineTypeFromCurve(
                lastEdgeStyle.curve_type,
              )}
              connectionLineStyle={connectionLineStyle}
              defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
              nodesDraggable={
                canEdit &&
                cursorInteraction.nodesDraggable &&
                !isPlacing &&
                !onboardingLocksViewport
              }
              elementsSelectable={
                cursorInteraction.elementsSelectable &&
                !isPlacing &&
                !onboardingLocksViewport
              }
              nodesFocusable={false}
              minZoom={CANVAS_ZOOM.minZoom}
              maxZoom={CANVAS_ZOOM.maxZoom}
              // Deliberately NO snapToGrid/snapGrid: xyflow quantizing
              // upstream hides sub-cell alignment stops from the guide
              // competition (and changes screenToFlowPosition semantics).
              // All lattice snapping lives in the helper-lines interceptor.
              defaultViewport={savedViewport ?? FALLBACK_VIEWPORT}
              onViewportChange={handleViewportChange}
              onMoveStart={handleMoveStart}
              onMoveEnd={handleMoveEnd}
              proOptions={RF_PRO_OPTIONS}
            >
              {showGrid && (
                <div className="canvas-cursor-glow-layer" aria-hidden>
                  <Background
                    id="cursor-glow"
                    variant={
                      gridMode === "dots"
                        ? BackgroundVariant.Dots
                        : BackgroundVariant.Lines
                    }
                    gap={CANVAS_SNAP.grid[0]}
                    size={
                      gridMode === "dots" ? CANVAS_GRID.dotGlowSize : undefined
                    }
                    // Dot centers on the line-grid vertices (xyflow's default
                    // offset fudge shifts dots half a cell; lines are immune).
                    offset={
                      gridMode === "dots"
                        ? CANVAS_GRID.dotGlowSize / 2
                        : undefined
                    }
                  />
                </div>
              )}
              {showGrid && (
                <Background
                  variant={
                    gridMode === "dots"
                      ? BackgroundVariant.Dots
                      : BackgroundVariant.Lines
                  }
                  gap={CANVAS_SNAP.grid[0]}
                  size={gridMode === "dots" ? CANVAS_GRID.dotSize : undefined}
                  // See glow layer: align dots to the line-grid vertices.
                  offset={
                    gridMode === "dots" ? CANVAS_GRID.dotSize / 2 : undefined
                  }
                />
              )}

              {/* Edge floating toolbar — single selected edge, no node selection */}
              <AnimatePresence>
                {selectedEdgeForToolbar && (
                  <EdgeFloatingToolbar
                    key={selectedEdgeForToolbar.id}
                    edge={selectedEdgeForToolbar}
                    onStyleChange={handleEdgeStyleChange}
                    onDelete={handleEdgeDelete}
                    onPopoverOpenChange={setToolbarPopoverOpen}
                  />
                )}
              </AnimatePresence>

              {/* Remote user live cursors via Liveblocks. The default cursor
                reads name/avatar/color via `useUser(userId)`, dispatched
                through the `resolveUsers` we configure on
                `<LiveblocksProvider>` (see App.tsx). Styled by
                `@liveblocks/react-ui/styles.css`. */}
              <Cursors />

              {/* Text Note Ghost Preview (FigJam-style cursor follower) - hide when hovering controls */}
              {isPlacingTextNote &&
                ghostNotePosition &&
                !isHoveringControls && (
                  <ViewportPortal>
                    <div
                      className="text-note-ghost-preview absolute pointer-events-none"
                      style={{
                        left: ghostNotePosition.x,
                        top: ghostNotePosition.y,
                        width: NOTE_NODE_WIDTH,
                        height: NOTE_NODE_HEIGHT,
                        // The ghost is the note itself, translucent — same
                        // fill, same corner, no placement ring.
                        borderRadius: "8px",
                        backgroundColor: DEFAULT_NOTE_COLOR,
                        opacity: 0.5,
                        boxShadow: "0 1px 0 rgba(15,12,8,0.12)",
                        transition: "opacity 0.15s ease-out",
                      }}
                    >
                      <div className="flex items-center justify-center h-full">
                        <span className="font-sans text-sm text-n-60">
                          Click to place
                        </span>
                      </div>
                    </div>
                  </ViewportPortal>
                )}

              {/* Doc Ghost Preview — file-card outline following the cursor */}
              {isPlacingDoc && ghostDocPosition && !isHoveringControls && (
                <ViewportPortal>
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: ghostDocPosition.x,
                      top: ghostDocPosition.y,
                      width: DOC_NODE_WIDTH,
                      height: DOC_NODE_HEIGHT,
                      border: "2px solid hsl(var(--primary))",
                      borderRadius: "8px",
                      backgroundColor: "hsl(var(--background))",
                      opacity: 0.5,
                      transition: "opacity 0.15s ease-out",
                    }}
                  >
                    <div className="flex h-full flex-col items-center justify-center gap-1.5">
                      <FileText className="h-6 w-6 text-foreground/70" />
                      <span className="text-sm font-medium text-foreground/70">
                        Click to place
                      </span>
                    </div>
                  </div>
                </ViewportPortal>
              )}

              {/* Quick chat / voice ghost — rust bubble centered on cursor */}
              {isPlacingDeferredQuickCompose &&
                deferredQuickComposeGhost &&
                !isHoveringControls && (
                  <DeferredQuickComposeGhostPreview
                    ghost={deferredQuickComposeGhost}
                  />
                )}

              {/* Placement owns every click on the board, node or not — a
                  drop onto a node is the case that anchors the pin, and
                  `onPaneClick` never sees it because the node takes it
                  first. Above the renderer (z 4), below the chrome (z 10),
                  which keeps taking its own clicks and its hover state. */}
              {isPlacingDeferredQuickCompose && (
                <div
                  className="absolute inset-0"
                  style={{ zIndex: 5 }}
                  onClick={(e) =>
                    commitDeferredQuickComposeAt(e.clientX, e.clientY)
                  }
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}

              {/* Canvas Text Ghost — plain label on hover, resizing rectangle while dragging */}
              {isPlacingTextLabel && textLabelGhost && !isHoveringControls && (
                <ViewportPortal>
                  <TextLabelPlacementGhost ghost={textLabelGhost} />
                </ViewportPortal>
              )}

              {isPlacingShape &&
                shapeGhost &&
                pendingShapeType &&
                !isHoveringControls && (
                  <ViewportPortal>
                    <div
                      className="absolute pointer-events-none"
                      style={{
                        left: shapeGhost.position.x,
                        top: shapeGhost.position.y,
                        width: shapeGhost.width,
                        height: shapeGhost.height,
                        opacity: 0.5,
                      }}
                    >
                      <Shape
                        type={pendingShapeType}
                        width={shapeGhost.width}
                        height={shapeGhost.height}
                        fill={activeShapeColor}
                        fillOpacity={activeShapeNoFill ? 0 : 1}
                        stroke={activeShapeStrokeColor}
                        strokeDasharray={
                          activeShapeStrokeStyle === "dashed"
                            ? "16 8"
                            : activeShapeStrokeStyle === "dotted"
                              ? "3 8"
                              : undefined
                        }
                      />
                    </div>
                  </ViewportPortal>
                )}

              {/* Section Ghost Preview */}
              {isPlacingSection && sectionGhost && !isHoveringControls && (
                <ViewportPortal>
                  <div
                    className="absolute pointer-events-none rounded-md border-2 border-dashed border-primary/50"
                    style={{
                      left: sectionGhost.position.x,
                      top: sectionGhost.position.y,
                      width: sectionGhost.width,
                      height: sectionGhost.height,
                    }}
                  />
                </ViewportPortal>
              )}

              {/* Connector Ghost Preview — only shown after tail is placed */}
              {isPlacingConnector &&
                connectorGhost?.tail &&
                !isHoveringControls &&
                (() => {
                  const { tail, head } = connectorGhost;
                  const curveType =
                    activeConnectorPreset?.curve_type ?? "straight";
                  let d: string;
                  if (curveType === "step") {
                    const mx = (tail.x + head.x) / 2;
                    d = `M ${tail.x} ${tail.y} L ${mx} ${tail.y} L ${mx} ${head.y} L ${head.x} ${head.y}`;
                  } else if (curveType === "smooth") {
                    const dx = Math.abs(head.x - tail.x) * 0.5;
                    d = `M ${tail.x} ${tail.y} C ${tail.x + dx} ${tail.y}, ${head.x - dx} ${head.y}, ${head.x} ${head.y}`;
                  } else {
                    d = `M ${tail.x} ${tail.y} L ${head.x} ${head.y}`;
                  }
                  return (
                    <ViewportPortal>
                      <svg
                        className="absolute pointer-events-none overflow-visible"
                        style={{ left: 0, top: 0, opacity: 0.6 }}
                      >
                        <path
                          d={d}
                          stroke={activeShapeStrokeColor}
                          strokeWidth={activeShapeStrokeWidth}
                          strokeLinecap="round"
                          strokeDasharray={
                            activeShapeStrokeStyle === "dashed"
                              ? "16 8"
                              : activeShapeStrokeStyle === "dotted"
                                ? "3 8"
                                : undefined
                          }
                          fill="none"
                        />
                        {/* Tail dot */}
                        <circle
                          cx={tail.x}
                          cy={tail.y}
                          r={4}
                          fill={activeShapeStrokeColor}
                        />
                      </svg>
                    </ViewportPortal>
                  );
                })()}

              <CanvasDrawLayer active={isDrawing} />

              {/* Share + canvas controls — top right, offset when chat overlaps
                  this band. The ease is for the discrete moves (the chat opens,
                  docks, closes); during a width drag the offset already arrives
                  every frame, and easing on top of it is what makes the row
                  trail the chat edge by the transition's own length. */}
              <div
                className={cn(
                  "absolute top-4 z-10 flex items-center gap-2",
                  !isChatResizing &&
                    "transition-[right] duration-200 ease-in-out",
                )}
                style={{ right: `calc(1rem + ${controlsRightOffset}px)` }}
                onMouseEnter={() => setIsHoveringControls(true)}
                onMouseLeave={() => setIsHoveringControls(false)}
              >
                {canvasId && canManageAssignments && (
                  <CanvasShareButton canvasId={canvasId} />
                )}
                <CanvasControlsToolbar />
              </div>

              {currentCanvas?.archived_at && canvasId && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-50 w-[min(640px,calc(100vw-8rem))]">
                  <InTrashBanner
                    kind="canvas"
                    parentKind={
                      currentCanvas.archived_with_resource_id
                        ? "workspace"
                        : undefined
                    }
                    isRestoring={
                      restoreCanvas.isPending || restoreWorkspace.isPending
                    }
                    onRestore={() => {
                      if (currentCanvas.archived_with_resource_id) {
                        restoreWorkspace.mutate(
                          currentCanvas.archived_with_resource_id,
                        );
                      } else {
                        restoreCanvas.mutate(canvasId);
                      }
                    }}
                    className="bg-background/95 backdrop-blur-lg shadow-lg shadow-black/10"
                  />
                </div>
              )}

              {/* Wrapper so controls stay on the left and never overlap the floating chat / FAB */}
              <div className="absolute inset-0 flex justify-start">
                <div
                  className="w-full h-full relative"
                  style={
                    controlsMaxWidth > 0
                      ? { maxWidth: controlsMaxWidth }
                      : undefined
                  }
                >
                  {/* Primary canvas toolbar — horizontal bottom or vertical left */}
                  {canEdit && (
                    <CanvasToolbarLayoutProvider>
                      {/* The toolbar's band: the space actually free of
                        floating chrome, running from the rail's inner edge
                        to the chat's. It carries its own left AND width
                        rather than inheriting the parent's — the parent is
                        already narrowed by the same clearances, so relying
                        on it would subtract the rail twice and centre the
                        toolbar half a rail too far left. The parent cannot
                        simply be shifted instead: the node context menu and
                        the quick composer are its siblings and position
                        themselves with viewport coordinates. */}
                      <div
                        className="pointer-events-none absolute inset-y-0 left-0"
                        style={{
                          width:
                            controlsMaxWidth > 0 ? controlsMaxWidth : undefined,
                          right: controlsMaxWidth > 0 ? undefined : 0,
                        }}
                      >
                        <div
                          data-canvas-control
                          className="pointer-events-auto"
                          onMouseEnter={() => setIsHoveringControls(true)}
                          onMouseLeave={() => setIsHoveringControls(false)}
                        >
                          <CanvasToolbar
                            onToggleTextNoteMode={(screenPos) => {
                              // Flyout unmount under the pointer can leave
                              // isHoveringControls stuck true and hide the ghost.
                              setIsHoveringControls(false);
                              startTextNotePlacement(screenPos);
                            }}
                            isPlacingTextNote={isPlacingTextNote}
                            onToggleDocMode={(screenPos) => {
                              // Flyout unmount under the pointer can leave
                              // isHoveringControls stuck true and hide the ghost.
                              setIsHoveringControls(false);
                              startDocPlacement(screenPos);
                            }}
                            isPlacingDoc={isPlacingDoc}
                            onDismissToolbarMenus={dismissToolbarMenus}
                            onToggleTextLabelMode={handleToggleTextLabelMode}
                            isPlacingTextLabel={isPlacingTextLabel}
                            onCancelPlacement={() => {
                              cancelAllPlacementModes();
                              dismissToolbarMenus();
                            }}
                            isPlacingSection={isPlacingSection}
                            onToggleSectionMode={() => {
                              setIsPlacingTextNote(false);
                              setIsPlacingDoc(false);
                              setIsPlacingTextLabel(false);
                              cancelPlacingShape();
                              cancelPlacingConnector();
                              dismissToolbarMenus();
                              handleToggleSectionMode();
                            }}
                            isDrawMenuOpen={isDrawMenuOpen}
                            isDrawing={isDrawing}
                            onToggleDrawMenu={() => {
                              dismissToolbarMenus();
                              setIsDrawMenuOpen(true);
                              setIsDrawing(true);
                            }}
                            onCloseDrawMenu={() => {
                              setIsDrawMenuOpen(false);
                              cancelDrawing();
                            }}
                            isShapesMenuOpen={isShapesMenuOpen}
                            isPlacingShape={isPlacingShape}
                            pendingShapeType={pendingShapeType}
                            onToggleShapesMenu={() => {
                              dismissToolbarMenus();
                              setIsShapesMenuOpen(true);
                              setIsPlacingTextNote(false);
                              setIsPlacingDoc(false);
                              setIsPlacingTextLabel(false);
                              cancelPlacingSection();
                              startPlacingShape(lastPlacedShape);
                            }}
                            onShapeSelect={(shapeType) => {
                              setIsPlacingTextNote(false);
                              setIsPlacingDoc(false);
                              setIsPlacingTextLabel(false);
                              cancelPlacingConnector();
                              cancelPlacingSection();
                              startPlacingShape(shapeType);
                            }}
                            isConnectorActive={isPlacingConnector}
                            activeConnectorPreset={activeConnectorPreset}
                            onConnectorSelect={(preset) => {
                              setIsPlacingTextNote(false);
                              setIsPlacingDoc(false);
                              setIsPlacingTextLabel(false);
                              cancelPlacingShape();
                              cancelPlacingSection();
                              setActiveConnectorPreset(preset);
                              startPlacingConnector(preset);
                            }}
                            onWidgetsMenuOpenChange={(open) => {
                              if (open) {
                                setIsShapesMenuOpen(false);
                                setIsLinkMenuOpen(false);
                                closeQuickAdd();
                                cancelDeferredQuickComposePlacement();
                              }
                              setIsWidgetsMenuOpen(open);
                            }}
                            isWidgetsMenuOpen={isWidgetsMenuOpen}
                            onLinkMenuOpenChange={(open) => {
                              if (open) {
                                setIsShapesMenuOpen(false);
                                setIsWidgetsMenuOpen(false);
                                closeQuickAdd();
                                cancelDeferredQuickComposePlacement();
                              }
                              setIsLinkMenuOpen(open);
                            }}
                            isLinkMenuOpen={isLinkMenuOpen}
                            insertMenu={{
                              ...toolbarInsertActions,
                              onAddFromLibrary,
                              onIntegrationEntitySelect:
                                toolbarIntegrationSelect,
                            }}
                          />
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept={CANVAS_FILE_UPLOAD_ACCEPT}
                            className="hidden"
                            onChange={handleFileSelect}
                          />
                          <input
                            // React drops unknown non-string DOM attributes,
                            // so set the directory-picker ones imperatively.
                            ref={(el) => {
                              folderInputRef.current = el;
                              if (el) {
                                el.setAttribute("webkitdirectory", "");
                                el.setAttribute("directory", "");
                              }
                            }}
                            type="file"
                            multiple
                            data-canvas-folder-input=""
                            className="hidden"
                            onChange={handleFolderSelect}
                          />
                        </div>
                      </div>
                    </CanvasToolbarLayoutProvider>
                  )}

                  <AnimatePresence>
                    {nodeContextMenu && canEdit && (
                      <CanvasNodeContextMenu
                        canvasId={canvasId ?? undefined}
                        nodeType={nodeContextMenu.nodeType}
                        entity={contextMenuEntity}
                        entityBase={contextMenuEntityBase}
                        fileRecord={contextMenuFileRecord}
                        cursorPosition={nodeContextMenu}
                        onClose={() => setNodeContextMenu(null)}
                        handlers={nodeContextMenuHandlers}
                        textNoteDownload={textNoteDownload}
                        state={nodeContextMenuState}
                      />
                    )}
                  </AnimatePresence>

                  {/* Quick Chat Composer (ephemeral) */}
                  <AnimatePresence>
                    {quickChat.composingState?.mode === "chat" && canvasId && (
                      <QuickChatComposer
                        position={quickChat.composingState.screenPosition}
                        canvasId={canvasId}
                        isSubmitting={quickChat.isSubmitting}
                        initialContent={quickChat.composingState.initialContent}
                        onSubmit={quickChat.handleSubmitQuickChat}
                        onCancel={quickChat.handleCancelComposing}
                      />
                    )}
                  </AnimatePresence>

                  {/* Quick Voice Composer (ephemeral) */}
                  <AnimatePresence>
                    {quickChat.composingState?.mode === "voice" && canvasId && (
                      <QuickVoiceComposer
                        position={quickChat.composingState.screenPosition}
                        onTranscriptionReady={
                          quickChat.handleTranscriptionReady
                        }
                        onSend={quickChat.handleTranscriptionAndSend}
                        onCancel={quickChat.handleCancelComposing}
                      />
                    )}
                  </AnimatePresence>
                </div>

                {/* Quick add / add-context menu — outside controls width cap */}
                <AnimatePresence>
                  {showQuickAdd && canvasId && canEdit && quickAddCursor && (
                    <CanvasInsertMenu
                      variant="anchored"
                      open
                      onOpenChange={(open) => {
                        if (!open) closeQuickAdd();
                      }}
                      anchor={{
                        cursorPosition: quickAddCursor,
                        anchorToRailTrigger: explorerAnchorToRailTrigger,
                      }}
                      {...anchoredInsertActions}
                      onAddFromLibrary={onAddFromLibraryAnchored}
                      onIntegrationEntitySelect={anchoredIntegrationSelect}
                      ingestContextMenuFiles={
                        anchoredInsertPlacement === "contextMenu"
                          ? ingestContextMenuFiles
                          : undefined
                      }
                      useInternalFileInput={
                        anchoredInsertPlacement === "contextMenu"
                      }
                    />
                  )}
                </AnimatePresence>

                {/* Onboarding spotlight — pulsing halo over a target
                  canvas-chrome element (e.g. toolbar buttons). The
                  onboarding widget node writes the target id into
                  CanvasOnboardingContext; this component portals to
                  document.body and tracks the target's screen rect. */}
                <OnboardingSpotlight />
                {canEdit && multiSelectNodes.length >= 2 && (
                  <MultiSelectFloatingToolbar
                    selectedNodes={multiSelectNodes}
                  />
                )}
                {/* Onboarding coach card — screen-docked portal. The
                  invisible OnboardingWidget node owns the persisted
                  step state and publishes it via
                  CanvasOnboardingContext.activeOnboarding; this
                  component reads that and renders the step UI in a
                  viewport-fixed card that re-anchors per step. */}
                <OnboardingOverlay />
              </div>
              {/* DS ground behavior: grid fades out 50%→30% zoom. */}
              {showGrid && <CanvasGridFade />}
              <ChromeCollisionBridge />
              <HelperLines />
              {canComment && (
                <>
                  <CanvasCommentPinLayer />
                  <CanvasCommentThreadWindow />
                  <CanvasCommentComposer />
                </>
              )}
            </ReactFlow>
          </CanvasChatSessionProvider>
        </div>
      </div>
    </>
  );
}

export const CanvasPage = withCanvasProvider(CanvasPageInner, {
  uiVariant: "interactive",
});
